import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "@/lib/auth";
import { Toaster } from "sonner";
import Login from "@/pages/Login";
import Dashboard from "@/pages/Dashboard";
import WeekDetail from "@/pages/WeekDetail";
import ParentPortal from "@/pages/ParentPortal";
import ParentDashboard from "@/pages/ParentDashboard";
import Admin from "@/pages/Admin";
import Students from "@/pages/Students";
import JoinClass from "@/pages/JoinClass";
import AcceptParentInvite from "@/pages/AcceptParentInvite";
import PrintRoster from "@/pages/PrintRoster";

function AuthLoading() {
  return (
    <div className="min-h-screen flex items-center justify-center text-slate-500">
      Loading...
    </div>
  );
}

function ProtectedRoute({ children, roles }) {
  const { user } = useAuth();
  if (user === null) return <AuthLoading />;
  if (!user) return <Navigate to="/login" replace />;
  if (roles && !roles.includes(user.role)) {
    // Parents land on /parent; everyone else on /
    return <Navigate to={user.role === "parent" ? "/parent" : "/"} replace />;
  }
  return children;
}

function PublicOnly({ children }) {
  const { user } = useAuth();
  if (user === null) return <AuthLoading />;
  if (user) return <Navigate to={user.role === "parent" ? "/parent" : "/"} replace />;
  return children;
}

function HomeRouter() {
  const { user } = useAuth();
  if (user === null) return <AuthLoading />;
  if (!user) return <Navigate to="/login" replace />;
  if (user.role === "parent") return <Navigate to="/parent" replace />;
  return <Dashboard />;
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <Routes>
            <Route path="/login" element={<PublicOnly><Login /></PublicOnly>} />
            {/* Public self-registration is disabled — accounts are created by
                the teacher in Admin, or students self-onboard via /join/:code
                with a class code. /register redirects to /login as a safety net
                for any old bookmarks or SEO stragglers. */}
            <Route path="/register" element={<Navigate to="/login" replace />} />
            <Route path="/parents/:code" element={<ParentPortal />} />
            <Route path="/join/:code" element={<PublicOnly><JoinClass /></PublicOnly>} />
            <Route path="/parent-invite/:token" element={<PublicOnly><AcceptParentInvite /></PublicOnly>} />
            <Route
              path="/parent"
              element={<ProtectedRoute roles={["parent"]}><ParentDashboard /></ProtectedRoute>}
            />
            <Route
              path="/admin"
              element={<ProtectedRoute roles={["teacher"]}><Admin /></ProtectedRoute>}
            />
            <Route
              path="/admin/print"
              element={<ProtectedRoute roles={["teacher"]}><PrintRoster /></ProtectedRoute>}
            />
            <Route
              path="/students"
              element={<ProtectedRoute roles={["teacher", "assistant"]}><Students /></ProtectedRoute>}
            />
            <Route path="/" element={<HomeRouter />} />
            <Route
              path="/week/:weekNum"
              element={
                <ProtectedRoute roles={["teacher", "assistant", "student"]}>
                  <WeekDetail />
                </ProtectedRoute>
              }
            />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
          <Toaster richColors position="top-right" />
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
